import { type Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import { Field, FieldLabel } from '@/components/shadcn/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { getPolicyRef } from '@/features/crm/utils/anva/utils';
import type { Party } from '@/features/party/types';
import usePolicies from '@/features/policies/queries/hooks/usePolicies';
import type { Policy } from '@/features/policies/types';

import PolicySelectItem from '../PolicySelectItem';

import type { ExportEmailValuesAnva } from './types';

const NO_POLICY = 'none';

type Props = {
  control: Control<ExportEmailValuesAnva>;
  party: Maybe<Party>;
};

export default function AnvaPolicyField({ control, party }: Props) {
  const { t } = useTranslation();

  const {
    analysis: {
      relevant: { policyIds },
    },
  } = useEmail();

  const { data: policies } = usePolicies(
    party?.id ?? '',
    { activeOnly: true },
    { enabled: Boolean(party?.id) },
  );

  const isRelevant = (policy: Policy) =>
    Boolean(policyIds?.includes(policy.id));

  const anvaPolicies = policies
    ?.filter((p) => p.external_refs?.find((r) => r.company === 'ANVA'))
    .sort((a, b) => Number(isRelevant(b)) - Number(isRelevant(a)));

  return (
    <Controller
      control={control}
      name="crmPolicyId"
      render={({ field }) => (
        <Field>
          <FieldLabel>{t('EXPORT_EMAIL_ANVA_POLICY_LABEL')}</FieldLabel>
          <Select
            value={field.value ?? NO_POLICY}
            onValueChange={(value) =>
              field.onChange(value === NO_POLICY ? undefined : value)
            }
          >
            <SelectTrigger className="w-full *:data-[slot=select-value]:flex-1">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value={NO_POLICY}>
                {t('EXPORT_EMAIL_ANVA_POLICY_NONE')}
              </SelectItem>

              {anvaPolicies?.map((policy) => (
                <PolicySelectItem
                  key={policy.id}
                  value={getPolicyRef(policy)}
                  policy={policy}
                  isRelevant={isRelevant(policy)}
                />
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
    />
  );
}
